import styled from '@emotion/styled';
import {
  FormControlLabel,
  Radio,
  RadioProps,
  Tooltip
} from '@material-ui/core';
import React from 'react';
import { useId } from '../../hooks/useId';

type RadioPanelOrientation = 'vertical' | 'horizontal';

type RadioPanelOption<T> = {
  label: React.ReactNode;
  description?: React.ReactNode;
  body?: React.ReactNode;
  tooltip?: React.ReactNode;
  disabled?: boolean;
  value: T;
};

const RadioGroupContainer = styled('div')<{
  orientation: 'vertical' | 'horizontal';
  maxItemsBeforeWrap?: number;
}>((p) => {
  const [flexBasis, maxWidth] = p.maxItemsBeforeWrap
    ? [
        `${Math.floor(100 / p.maxItemsBeforeWrap)}%`,
        `${Math.floor(100 / p.maxItemsBeforeWrap)}%`
      ]
    : [0, 'auto'];
  return {
    display: 'flex',
    alignItems: 'stretch',
    flexDirection: p.orientation === 'horizontal' ? 'row' : 'column',
    flexWrap: 'wrap',

    '> *': {
      flex: `1 1 ${flexBasis}`,
      maxWidth
    }
  };
});

type Variant = 'dense' | 'normal' | 'compact';

const RadioItemContainer = styled('label')<{
  checked: boolean;
  checkedColor: 'primary' | 'secondary' | 'default';
  variant: Variant;
}>((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'max-content 1fr',
  border: p.variant === 'compact' ? 'none' : p.theme.custom.border.standard,
  borderColor: p.checked
    ? p.checkedColor === 'default'
      ? 'currentcolor'
      : p.theme.palette[p.checkedColor].main
    : p.theme.custom.colors.gray.w300,
  padding: p.theme.spacing(p.variant === 'compact' ? 1 : 3),
  paddingLeft: p.theme.spacing(2),
  cursor: 'pointer',
  zIndex: p.checked ? 1 : 0,
  boxShadow:
    p.checked && p.variant !== 'compact'
      ? 'inset 0px 0px 0px 1px ' +
        (p.checkedColor === 'default'
          ? 'currentcolor'
          : p.theme.palette[p.checkedColor].main)
      : 'none',

  ':not(:first-of-type)': {
    marginTop: -1
  },

  ':first-of-type': {
    borderTopLeftRadius: p.theme.custom.border.radius(),
    borderTopRightRadius: p.theme.custom.border.radius()
  },

  ':last-child': {
    borderBottomLeftRadius: p.theme.custom.border.radius(),
    borderBottomRightRadius: p.theme.custom.border.radius()
  }
}));

const RadioItemHeader = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center'
}));

const RadioItemDescription = styled('div')((p) => ({
  paddingLeft: p.theme.spacing(),
  marginTop: p.theme.spacing(),
  color: p.theme.custom.colors.gray.w500,
  display: 'grid'
}));

const RadioItemBody = styled('div')<{
  variant: Variant;
}>((p) => ({
  paddingLeft: p.theme.spacing(),
  marginTop: p.theme.spacing(p.variant === 'dense' ? 0 : 2)
}));

const DEFAULT_COLOR = 'primary';

const RadioItem = ({
  checked,
  description,
  body,
  label,
  name,
  onSelect,
  color = DEFAULT_COLOR,
  variant,
  disabled,
  tooltip
}: {
  checked: boolean;
  description?: React.ReactNode;
  body?: React.ReactNode;
  label: React.ReactNode;
  name: string;
  onSelect: () => void;
  color?: RadioProps['color'];
  size?: RadioProps['size'];
  variant: Variant;
  disabled?: boolean;
  tooltip?: React.ReactNode;
}) => {
  const id = useId();
  return (
    <Tooltip title={tooltip || ''} placement="right">
      <RadioItemContainer
        htmlFor={id}
        checked={checked}
        checkedColor={color}
        variant={variant}
      >
        <RadioItemHeader>
          <FormControlLabel
            value="female"
            control={
              <Radio
                checked={checked}
                onChange={(ev) => ev.target.checked && onSelect()}
                name={name}
                color={color}
                disabled={disabled}
                id={id}
              />
            }
            label={label}
          />
        </RadioItemHeader>
        <div>
          {description && (variant !== 'compact' || checked) && (
            <RadioItemDescription>{description}</RadioItemDescription>
          )}
          {body && <RadioItemBody variant={variant}>{body}</RadioItemBody>}
        </div>
      </RadioItemContainer>
    </Tooltip>
  );
};

// TODO
// - Cannot rely on RadiuGroup, so that we get more control
//   over the radio buttons themself (without using FormControlLabel)
// - Example: https://material-ui.com/components/radio-buttons/#standalone-radio-buttons

export const RadioPanel = <T extends any>({
  value,
  onChange,
  options,
  color = DEFAULT_COLOR,
  orientation = 'horizontal',
  variant = 'normal',
  maxItemsBeforeWrap
}: {
  value: T;
  onChange: (nextValue: T) => void;
  options: RadioPanelOption<T>[];
  color?: RadioProps['color'];
  orientation?: RadioPanelOrientation;
  variant?: Variant;
  maxItemsBeforeWrap?: number;
}) => {
  const radioGroupName = useId();
  return (
    <RadioGroupContainer
      orientation={orientation}
      maxItemsBeforeWrap={maxItemsBeforeWrap}
    >
      {options.map((o, i) => (
        <RadioItem
          key={i}
          checked={o.value === value}
          onSelect={() => onChange(o.value)}
          label={o.label}
          description={o.description}
          body={o.body}
          name={radioGroupName}
          color={color}
          variant={variant}
          disabled={o.disabled}
          tooltip={o.tooltip}
        />
      ))}
    </RadioGroupContainer>
  );
};
